import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { ConnectivityPanel } from '../components/ConnectivityPanel';
import { DetachPanel } from '../components/DetachPanel';
import { FirewallPanel } from '../components/FirewallPanel';
import { FirewallZonesPanel } from '../components/FirewallZonesPanel';
import { RegistrationPanel } from '../components/RegistrationPanel';
import { PathSelectionPanel } from '../components/PathSelectionPanel';
import { PolicyRoutingPanel } from '../components/PolicyRoutingPanel';
import { DeviceQosPanels } from '../components/QosPanel';
import { WanHealthPanel } from '../components/WanHealthPanel';
import { ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Certificate {
  id: string;
  serial: string;
  status: string;
  notAfter: string;
  createdAt: string;
}
interface DeviceDetail {
  id: string;
  name: string;
  status: string;
  platform: string | null;
  arch: string | null;
  agentVersion: string | null;
  enrolledAt: string | null;
  lastSeenAt: string | null;
  lastInventoryAt: string | null;
  capabilities: Record<string, unknown>;
  inventory: Record<string, unknown> | null;
  inventoryCollectedAt: string | null;
  certificates: Certificate[];
}

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : '—';

export function DeviceDetailPage(): JSX.Element {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useAsync(
    () => api.get<DeviceDetail>(`/edge-devices/${id}/detail`),
    [id],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data) return <ErrorState message={t('errors.notFound')} />;

  return (
    <section>
      <div className="page-head">
        <h1>
          <Link to="/devices">{t('nav.devices')}</Link> / {data.name}
        </h1>
        <span className={`chip chip--${data.status.toLowerCase()}`}>{data.status}</span>
      </div>

      <RegistrationPanel deviceId={data.id} />

      <WanHealthPanel deviceId={data.id} />

      <ConnectivityPanel deviceId={data.id} />

      <FirewallZonesPanel deviceId={data.id} />

      <FirewallPanel deviceId={data.id} />

      <PolicyRoutingPanel deviceId={data.id} />

      <PathSelectionPanel deviceId={data.id} />

      <DeviceQosPanels deviceId={data.id} />

      <DetachPanel deviceId={data.id} />

      <div className="card">
        <h2>{t('device.detail')}</h2>
        <dl className="kv">
          <dt>{t('device.platform')}</dt>
          <dd>
            {data.platform ?? '—'} {data.arch ? `(${data.arch})` : ''}
          </dd>
          <dt>{t('device.agentVersion')}</dt>
          <dd>{data.agentVersion ?? '—'}</dd>
          <dt>{t('device.enrolledAt')}</dt>
          <dd>{fmt(data.enrolledAt)}</dd>
          <dt>{t('device.lastSeen')}</dt>
          <dd>{fmt(data.lastSeenAt)}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>{t('device.capabilities')}</h2>
        <pre className="code-block">{JSON.stringify(data.capabilities, null, 2)}</pre>
      </div>

      <div className="card">
        <h2>
          {t('device.inventory')}{' '}
          <span className="topbar__tagline">{fmt(data.inventoryCollectedAt)}</span>
        </h2>
        {data.inventory ? (
          <pre className="code-block">{JSON.stringify(data.inventory, null, 2)}</pre>
        ) : (
          <p className="topbar__tagline">{t('device.noInventory')}</p>
        )}
      </div>

      <div className="card">
        <h2>{t('device.certificates')}</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Serial</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Issued</th>
              </tr>
            </thead>
            <tbody>
              {data.certificates.map((c) => (
                <tr key={c.id}>
                  <td>
                    <code>{c.serial.slice(0, 16)}…</code>
                  </td>
                  <td>
                    <span className={`chip chip--${c.status.toLowerCase()}`}>{c.status}</span>
                  </td>
                  <td>{fmt(c.notAfter)}</td>
                  <td>{fmt(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
